import { oc } from '@orpc/contract';
import * as z from 'zod';
import { remoteCommandTypeSchema } from '../enums/command.js';
import { contentItemIdSchema, deviceIdSchema, remoteCommandIdSchema } from '../primitives/id.js';
import { instantSchema } from '../primitives/time.js';
import { liveDeviceStateSchema } from './sessions.js';
import { API_VERSION } from './version.js';
const base = `/${API_VERSION}/commands`;
const devices = `/${API_VERSION}/devices`;
export const remoteCommandSchema = z.object({
    id: remoteCommandIdSchema,
    deviceId: deviceIdSchema,
    type: remoteCommandTypeSchema,
    /** `ADD_TIME` carries `seconds`, `PLAY_CONTENT` carries `contentItemId`; the rest nothing. */
    payload: z.object({
        seconds: z.int().positive().optional(),
        contentItemId: contentItemIdSchema.optional(),
    }),
    issuedAt: instantSchema,
    expiresAt: instantSchema,
});
export const commandsContract = {
    /**
     * Issues a command to a device.
     *
     * The identifier is minted by the caller so that a retry after a lost
     * response cannot produce a second command. `ADD_TIME` in particular must not
     * be applied twice because a phone lost signal mid-tap.
     *
     * Whether it will actually arrive is reported rather than assumed: a command
     * for an offline device is queued, dropped or coalesced according to its type
     * (SPEC.md §18.1), and the parent interface says which.
     */
    issue: oc
        .route({ method: 'POST', path: base })
        .input(z.object({
        commandId: remoteCommandIdSchema,
        deviceId: deviceIdSchema,
        type: remoteCommandTypeSchema,
        seconds: z.int().positive().max(3600).optional(),
        /** What `PLAY_CONTENT` opens. Must be approved for the tablet's child. */
        contentItemId: contentItemIdSchema.optional(),
    }))
        .output(z.object({
        accepted: z.boolean(),
        /** What happens to it while the device is unreachable. */
        offlineDelivery: z.enum(['DROP', 'QUEUE', 'COALESCE']),
        deviceIsOnline: z.boolean(),
    }))
        .errors({
        DEVICE_NOT_FOUND: { status: 404, message: 'Device not found' },
        SECONDS_REQUIRED: { status: 400, message: 'ADD_TIME requires a number of seconds' },
        CONTENT_REQUIRED: { status: 400, message: 'PLAY_CONTENT requires a content item' },
        CONTENT_NOT_APPROVED: {
            status: 403,
            message: "This item is not approved for the device's child",
        },
    }),
    /** Confirms a device applied a command, which is what makes delivery auditable. */
    acknowledge: oc
        .route({ method: 'POST', path: `${base}/{commandId}/ack` })
        .input(z.object({ commandId: remoteCommandIdSchema }))
        .output(z.object({ success: z.literal(true) }))
        .errors({ COMMAND_NOT_FOUND: { status: 404, message: 'Command not found' } }),
    /**
     * Pending commands for the calling device.
     *
     * The realtime channel is the fast path; this is what a tablet asks after
     * reconnecting, and what keeps a queued `ADD_TIME` from being lost because a
     * stream was down when a parent granted it.
     */
    pending: oc
        .route({ method: 'GET', path: `${base}/pending` })
        .output(z.object({ commands: z.array(remoteCommandSchema) })),
};
export const deviceStateContract = {
    /**
     * The live state a parent dashboard renders.
     *
     * Polled rather than streamed. The tablet needs a stream because a pause has
     * to reach it now; a dashboard is a view, and a view that is a few seconds
     * behind is not wrong. The countdown a parent watches can be interpolated
     * locally between polls with `computeRemaining` from `@kidshub/policy` — the
     * same function the gate uses — so the number moves smoothly without either
     * side inventing its own arithmetic.
     */
    get: oc
        .route({ method: 'GET', path: `${devices}/{deviceId}/state` })
        .input(z.object({ deviceId: deviceIdSchema }))
        .output(liveDeviceStateSchema)
        .errors({
        DEVICE_NOT_FOUND: { status: 404, message: 'Device not found' },
        /**
         * Paired, but no child assigned yet. Not an error in the sense of
         * something going wrong — a tablet an adult has not finished setting up
         * has no live state to report, and saying so is more honest than
         * reporting an empty one.
         */
        DEVICE_UNPAIRED: { status: 409, message: 'Device has no active child' },
    }),
};
//# sourceMappingURL=commands.js.map